  import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserAuthenticationServices } from 'src/app/services/user-authentication.services';
import { HTTPService } from 'src/app/services/http.service';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface FlowConfigData {
  idConf?: number;
  confName?: string;
  showCodeCompany?: boolean;
  codeCompany?: number;
  newMode?: boolean;   // derivado de MKTPCFG.NEWMODE da config escolhida
}

export interface MktpCfgItem {
  IDCONF: number;
  NAME: string;
  CODECOMPANY: number;
  CODECOMPANY_NAME: string;
  CODEMKTP: number;
  CODEMKTP_NAME: string;
  ACTIVE: string;
  NEWMODE?: string;   // 'S' = motor novo (NEWMODE/ta-flow); 'N'/null = antigo
}

@Component({
  selector: 'app-flow-config-modal',
  templateUrl: './flow-config-modal.component.html',
  styleUrls: ['./flow-config-modal.component.scss'],
  standalone: false
})
export class FlowConfigModalComponent implements OnInit, OnDestroy {
  
  form: FormGroup;
  userCompanies: any[];
  
  // Lista de configurações
  allConfigurations: MktpCfgItem[] = [];
  filteredConfigurations: MktpCfgItem[] = [];
  selectedConfiguration: MktpCfgItem | null = null;
  
  loading = false;
  searchText = '';
  
  private searchSubscription: Subscription;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<FlowConfigModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FlowConfigData,
    public uas: UserAuthenticationServices,
    private http: HTTPService
  ) {
    this.userCompanies = this.uas.user.companylist || [];
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      idConf: [this.data?.idConf || null, Validators.required],
      confName: [this.data?.confName || ''],
      showCodeCompany: [this.data?.showCodeCompany || false],
      codeCompany: [this.data?.codeCompany || null]
    });

    // Limpar codeCompany quando showCodeCompany for ativado
    this.form.get('showCodeCompany').valueChanges.subscribe(value => {
      if (value) {
        this.form.patchValue({ codeCompany: null });
      }
    });

    // Carregar configurações
    this.loadConfigurations();
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  /**
   * Carrega configurações MKTPCFG do backend
   */
  loadConfigurations(): void {
    this.loading = true;
    
    const codecompany = this.uas.user.codecompany;
    const ob = {
      entityName: 'Mktpcfg',
      sql: `SELECT 
              A.IDCONF,
              A.NAME,
              A.CODECOMPANY, 
              B.NAME AS CODECOMPANY_NAME,
              A.CODEMKTP,
              D.NAME AS CODEMKTP_NAME,
              A.ACTIVE,
              A.NEWMODE
            FROM MKTPCFG A (NOLOCK)
              INNER JOIN MKTPCOMPANIES B (NOLOCK) ON A.CODECOMPANY = B.CODECOMPANY
              INNER JOIN MKTPLACES D (NOLOCK) ON A.CODEMKTP = D.CODEMKTP
            WHERE ${this.uas.user.profile === 'ADMIN' ? '0=0' : `A.CODECOMPANY IN (${this.uas.getCompanies()})`}
              AND A.ONLINE = 'S'
              
            ORDER BY A.NAME`
    };

    this.http.post(ob, 'JsonBrokerServices/findnative', false, true, true).subscribe({
      next: (response: any) => {
        this.allConfigurations = response?.SQL || [];
        this.filteredConfigurations = [...this.allConfigurations];
        
        // Se já tem um IDCONF selecionado, buscar o nome
        if (this.data?.idConf) {
          this.selectedConfiguration = this.allConfigurations.find(c => c.IDCONF === this.data.idConf) || null;
          if (this.selectedConfiguration) {
            this.form.patchValue({ confName: this.selectedConfiguration.NAME });
          }
        }
        
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar configurações:', error);
        this.loading = false;
      }
    });
  }

  /**
   * Filtra configurações baseado na busca
   */
  onSearchChange(searchText: string): void {
    this.searchText = searchText.toLowerCase();
    
    if (!this.searchText) {
      this.filteredConfigurations = [...this.allConfigurations];
    } else {
      this.filteredConfigurations = this.allConfigurations.filter(config => 
        config.NAME.toLowerCase().includes(this.searchText) ||
        config.IDCONF.toString().includes(this.searchText) ||
        config.CODEMKTP_NAME.toLowerCase().includes(this.searchText)
      );
    }
  }

  /**
   * Seleciona uma configuração da lista
   */
  selectConfiguration(config: MktpCfgItem): void {
    this.selectedConfiguration = config;
    this.form.patchValue({
      idConf: config.IDCONF,
      confName: config.NAME
    });
    this.searchText = config.NAME;
    this.filteredConfigurations = [...this.allConfigurations];
  }

  /**
   * Limpa a seleção
   */
  clearSelection(): void {
    this.selectedConfiguration = null;
    this.form.patchValue({
      idConf: null,
      confName: ''
    });
    this.searchText = '';
    this.filteredConfigurations = [...this.allConfigurations];
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  /** Motor da config selecionada: true = NEWMODE (novo/async), false = antigo. */
  get isSelectedNewMode(): boolean {
    return this.selectedConfiguration?.NEWMODE === 'S';
  }

  onSave(): void {
    if (this.form.valid) {
      this.dialogRef.close({ ...this.form.value, newMode: this.isSelectedNewMode });
    }
  }

  get isValid(): boolean {
    return this.form.valid && this.selectedConfiguration !== null;
  }
}

